import { ExecutorEntity, Pipeline, PipelineEnvironment, PipelineRepository, PipelineStep } from '@telespot/domain';
import { PipelineStepDraftProjector, PipelineStepStructuredView } from '@protocols/domain';

import { LabelNamesDto } from '../../dtos/label-names.dto';
import {
  PipelineStepDraftDto,
  PipelineStepManualDraftDto,
  PipelineStepStructuredDraftDto,
} from '../../dtos/pipeline-step-draft.dto';
import type { ILabelNamesPort } from '../../ports/label-names.port';

/**
 * Flat, UI-ready projection of a pipeline together with its ordered steps.
 *
 * Steps are returned as `PipelineStepDraftDto` instances (sorted by `order`
 * ascending) so the editor can seed its state without re-mapping. Category and
 * option names are resolved here, in one bulk read over the whole pipeline, so
 * these drafts carry real names rather than the uuid placeholders the draft
 * projector emits. A uuid with no stored label keeps its uuid as its name — it
 * stays visible and correctable rather than disappearing, and the editor
 * reports it as a badly-defined label. A lookup that fails leaves every name a
 * placeholder for the same reason.
 *
 * @group Protocols
 * @category Payloads
 */
export interface RetrievePipelineDetailOutput {
  id: string;
  name: { [lang: string]: string };
  description: { [lang: string]: string };
  env: PipelineEnvironment;
  steps: PipelineStepDraftDto[];
  createdAt: Date;
}

/**
 * Token-style error codes thrown by the retrieve-pipeline-detail query.
 *
 * Following the documented command/query error pattern: the enum value is
 * a stable namespaced identifier callers can disambiguate without parsing
 * free-form messages.
 */
export enum RetrievePipelineDetailErrorCode {
  PIPELINE_NOT_FOUND = 'retrieve_pipeline_detail.pipeline_not_found',
  REPOSITORY_ERROR = 'retrieve_pipeline_detail.repository_error',
}

/**
 * Thrown when retrieving a pipeline detail fails.
 *
 * `details` carries structured context (e.g. `{ pipelineId }`) so callers
 * can render or log the failure without scraping the message.
 */
export class RetrievePipelineDetailException extends Error {
  constructor(
    public readonly code: RetrievePipelineDetailErrorCode,
    public readonly details: Record<string, string> = {}
  ) {
    super(code.toString());
    this.name = 'RetrievePipelineDetailException';
    Object.setPrototypeOf(this, RetrievePipelineDetailException.prototype);
  }
}

/**
 * Use case to retrieve a pipeline together with its steps.
 */
export interface IRetrievePipelineDetailQuery {
  /**
   * Retrieve the pipeline and its steps in parallel, then resolve every label
   * name they reference in a single bulk read.
   * @param pipelineId The id of the pipeline to retrieve.
   * @returns The pipeline metadata as flat fields plus its steps as drafts, sorted by `order` ascending.
   * @throws RetrievePipelineDetailException when reading the pipeline or its
   * steps fails. A failed label-name lookup does NOT throw: the drafts come
   * back with their uuid placeholders instead, since losing the names must not
   * cost the whole editing session.
   */
  execute(pipelineId: string): Promise<RetrievePipelineDetailOutput>;
}

/** A step beside the structured view of its params, or `null` for a manual one. */
interface ProjectedStep {
  step: PipelineStep;
  params: Record<string, unknown>;
  view: PipelineStepStructuredView | null;
}

export class RetrievePipelineDetailQuery implements IRetrievePipelineDetailQuery {
  constructor(
    private readonly pipelineRepository: PipelineRepository,
    private readonly labelNamesPort: ILabelNamesPort
  ) {}

  public async execute(pipelineId: string): Promise<RetrievePipelineDetailOutput> {
    try {
      const [pipeline, steps] = await Promise.all([
        this.pipelineRepository.getById(pipelineId),
        this.pipelineRepository.findStepsForPipeline(pipelineId),
      ]);
      // Projected once, up front: the uuids to look up come from the same views
      // the drafts are built out of, so a step cannot be named against a
      // projection different from the one it ships.
      const projected = [...steps].sort((a, b) => a.order - b.order).map((step) => this.toProjectedStep(step));
      return this.toOutput(pipeline, projected, await this.resolveNames(projected));
    } catch (error) {
      throw this.toException(error, pipelineId);
    }
  }

  /**
   * The names for every uuid the pipeline references, or none at all if the
   * read failed.
   *
   * Deliberately swallowing: `ILabelNamesPort` leaves it to each caller whether
   * to fail or degrade, and here the names are the only thing lost. The drafts
   * keep their uuid placeholders — the same thing the editor showed before this
   * read resolved anything — so an unreachable label table costs a readable
   * category list rather than the whole editing session. Reporting the failure
   * belongs to the step editor, which resolves what it was handed and can tell
   * the administrator about it.
   *
   * It also keeps a Parse "Object not found" from the label read out of
   * `isNotFoundError`, which would otherwise announce a perfectly healthy
   * pipeline as missing.
   */
  private async resolveNames(projected: ProjectedStep[]): Promise<LabelNamesDto> {
    try {
      return await this.labelNamesPort.resolve(this.collectLabelUuids(projected));
    } catch {
      return {};
    }
  }

  private toProjectedStep(step: PipelineStep): ProjectedStep {
    const params: Record<string, unknown> = step.params ?? {};
    return { step, params, view: PipelineStepDraftProjector.toStructuredView(params) };
  }

  /**
   * Every label uuid the pipeline's structured steps reference, deduplicated so
   * a category shared between steps is read once, and with blanks dropped so
   * hand-edited params cannot send an empty uuid into the read.
   *
   * Manual-mode steps carry no structured view and so contribute nothing: their
   * params are round-tripped verbatim and never rendered as named rows.
   */
  private collectLabelUuids(projected: ProjectedStep[]): string[] {
    const uuids = new Set<string>();
    for (const { view } of projected) {
      for (const category of view?.categories ?? []) {
        for (const uuid of category.labelUuids()) {
          if (uuid) uuids.add(uuid);
        }
      }
    }
    return Array.from(uuids);
  }

  private toException(error: unknown, pipelineId: string): RetrievePipelineDetailException {
    if (this.isNotFoundError(error)) {
      return new RetrievePipelineDetailException(RetrievePipelineDetailErrorCode.PIPELINE_NOT_FOUND, { pipelineId });
    }
    return new RetrievePipelineDetailException(RetrievePipelineDetailErrorCode.REPOSITORY_ERROR, { pipelineId });
  }

  private isNotFoundError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    return /object not found/i.test(error.message);
  }

  private toOutput(
    pipeline: Pipeline,
    projected: ProjectedStep[],
    namesByUuid: Readonly<LabelNamesDto>
  ): RetrievePipelineDetailOutput {
    return {
      id: pipeline.id,
      name: { ...pipeline.name },
      description: { ...(pipeline.description ?? {}) },
      env: pipeline.env,
      steps: projected.map((entry) => this.toDraft(entry, namesByUuid)),
      createdAt: pipeline.createdAt,
    };
  }

  private toDraft({ step, params, view }: ProjectedStep, namesByUuid: Readonly<LabelNamesDto>): PipelineStepDraftDto {
    if (view === null) {
      return this.toManualDraft(step, params);
    }
    return this.toStructuredDraft(step, view, namesByUuid);
  }

  private toStructuredDraft(
    step: PipelineStep,
    view: PipelineStepStructuredView,
    namesByUuid: Readonly<LabelNamesDto>
  ): PipelineStepStructuredDraftDto {
    return {
      mode: 'structured',
      id: step.id,
      pipelineId: step.pipelineId,
      order: step.order,
      task: step.task,
      executorEntity: step.executorEntity,
      executorId: step.executorEntity === ExecutorEntity.ALGORITHM ? step.executorId : undefined,
      trigger: step.trigger,
      generateFinding: step.generateFinding,
      categories: view.categories.map((category) => category.withResolvedNames(namesByUuid)),
      multiple: view.multiple,
      allowNewOptions: view.allowNewOptions,
      enableMosaicView: view.enableMosaicView,
      counter: view.counter,
    };
  }

  private toManualDraft(step: PipelineStep, params: Record<string, unknown>): PipelineStepManualDraftDto {
    return {
      mode: 'manual',
      id: step.id,
      pipelineId: step.pipelineId,
      order: step.order,
      task: step.task,
      executorEntity: step.executorEntity,
      executorId: step.executorEntity === ExecutorEntity.ALGORITHM ? step.executorId : undefined,
      trigger: step.trigger,
      generateFinding: step.generateFinding,
      params,
    };
  }
}
